import { CredentialsService } from '@app/core/authentication/credentials.service';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer } from '@angular/platform-browser';
import { AuthenticationService, HttpService } from '@app/core';
import { TrackingService } from '@app/core/tracking.service';
import { BaseComponent } from '@app/core/helperclasses/basecomponent';
import { QuestionDialogComponent } from '@app/dialogs/question-dialog.ts/question-dialog.component';
import { VideoQuestionSetDialogComponent } from '@app/dialogs/video-question-set-dialog.ts/video-question-set-dialog.component';
import { ID } from '@app/models';
import { Question } from '@app/models/question';
import { Video, VideoSettings, VideoUserData } from '@app/models/video';
import { UntilDestroy } from '@ngneat/until-destroy';
import Vimeo from '@vimeo/player';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const YT: any;

interface YouTubeStateChangeEvent {
  data: number;
}

export interface UserVideosWatchTime {
  user_id: ID;
  videos_watch_time_list: UserVideosWatchTimeListItem[];
}

export interface UserVideosWatchTimeListItem {
  id: ID;
  watched_seconds: number;
}

@UntilDestroy()
@Component({
  selector: 'video-player-module',
  templateUrl: './video-player-module.component.html',
  styleUrls: ['./video-player-module.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VideoPlayerModuleComponent
  extends BaseComponent
  implements OnInit
{
  @Input() videoQuestionsUrl!: string;
  @Input() videoQuestionSetUrl?: string;
  @Input() hasMultipleQuestionsAfterVideo: boolean = false;
  @Input() videoSettings?: VideoSettings;
  @Input() videoWatchUrl!: string;
  @Input() videoData!: Video;
  @Input() courseId!: ID;
  @Input() endOfPlaylist: boolean = true;
  @Input() isAutoPlayVideo: boolean = false;
  @Input() hasLearningArea: boolean = false;
  @Input() hasNextLesson: boolean = false;

  @Output() nextVideo: EventEmitter<boolean> = new EventEmitter();
  @Output() nextLesson: EventEmitter<boolean> = new EventEmitter();
  @Output() tabChange: EventEmitter<number> = new EventEmitter();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  player!: Vimeo | any | undefined;
  watchedSeconds: number = 0;
  public videoCompleted: boolean = false;
  replay = false;
  private videoStartTracked = false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  videosWatchTimeList!: UserVideosWatchTime;
  localWatchedSeconds: number = 0;
  timeUpdateHandler!: Vimeo.EventCallback<Vimeo.TimeEvent> | undefined;
  endedEventHandler!: Vimeo.EventCallback<Vimeo.TimeEvent> | undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  timeUpdateInterval!: any;
  @ViewChild('iframe') iframe!: ElementRef<HTMLIFrameElement>;

  constructor(
    private renderer: Renderer2,
    private el: ElementRef,
    public sanitizer: DomSanitizer,
    private http: HttpService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private credentialsService: CredentialsService,
    private authService: AuthenticationService,
    private trackingService: TrackingService,
  ) {
    super();
  }

  private trackVideoStartOnce() {
    if (this.videoStartTracked) return;
    this.videoStartTracked = true;
    // IDs sind Hashes; videoWatchUrl liefert Kurs-/Lektions-Hash (Backend dekodiert).
    this.trackingService.trackVideoStart(
      String(this.videoData?.id),
      this.videoWatchUrl,
    );
  }

  ngOnInit() {
    this.renderer.addClass(this.el.nativeElement, 'video-player-module');
    //Tmp Code to Remove replace old videosWatchTimeList without userId
    const tmpVideosWatchTimeList = JSON.parse(
      localStorage.getItem('videosWatchTimeList') || '[]',
    );
    if (tmpVideosWatchTimeList) {
      if (tmpVideosWatchTimeList instanceof Array) {
        const newTmpVideosWatchTimeList = {
          user_id: this.credentialsService.credentials?.id ?? '',
          videos_watch_time_list: tmpVideosWatchTimeList,
        };
        localStorage.setItem(
          'videosWatchTimeList',
          JSON.stringify(newTmpVideosWatchTimeList),
        );
      }
    }

    this.videosWatchTimeList = JSON.parse(
      localStorage.getItem('videosWatchTimeList') ||
        JSON.stringify({
          user_id: this.credentialsService.credentials?.id ?? '',
          videos_watch_time_list: [],
        }),
    );
    if (
      this.videosWatchTimeList.user_id !=
      this.credentialsService.credentials?.id
    ) {
      this.videosWatchTimeList = {
        user_id: this.credentialsService.credentials?.id ?? '',
        videos_watch_time_list: [],
      };
    }
    this.getVideoFromLocalStorage(this.videoData.id);
    this.authService.currentVideoId = this.videoData.id;

    this.addSubscription(this.credentialsService.forceLogoutWarning$).subscribe(
      (res) => {
        if (res && res === true) {
          this.pauseVideo();
        }
      },
    );
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    if (this.timeUpdateInterval) {
      clearInterval(this.timeUpdateInterval);
    }
  }

  onIframeLoad() {
    if (this.replay) {
      if (this.videoData?.video_type.toLowerCase() === 'vimeo') {
        const iframe = this.el.nativeElement.querySelector('iframe');
        this.player = new Vimeo(iframe);
        // Remove previous event handlers
        this.player.off('timeupdate', this.timeUpdateHandler);
        this.player.off('ended', this.endedEventHandler);

        this.player.setCurrentTime(0);

        if (this.isAutoPlayVideo) {
          this.player.play();
        }

        // Reattach event handlers
        if (this.timeUpdateHandler) {
          this.player.on('timeupdate', this.timeUpdateHandler);
        }

        if (this.endedEventHandler) {
          this.player.on('ended', this.endedEventHandler);
        }
      }
      this.replay = false;
    }
  }

  pauseVideo() {
    this.player?.pause();
  }

  ngAfterViewInit() {
    this.initVideoWatchTime();
    this.iframe.nativeElement.addEventListener(
      'load',
      this.onIframeLoad.bind(this),
    );
  }

  // ngOnChanges(changes: SimpleChanges): void {
  //   if (changes) {
  //     if (this.player) {
  //       if (this.timeUpdateHandler) {
  //         this.player.off('timeupdate', this.timeUpdateHandler)
  //       }
  //       if (this.endedEventHandler) {
  //         this.player.off('ended', this.endedEventHandler);
  //       }
  //     }
  //

  //     this.player = undefined
  //     this.watchedSeconds = 0
  //     setTimeout(() => {
  //       this.initVideoWatchTime()
  //     });
  //   }
  // }

  syncVideoProgress(url: string, videoListId?: ID) {
    url = url.replace('{videoListId}', videoListId as string);
    const userWatchData: VideoUserData = {
      watched_seconds: this.watchedSeconds,
    };

    this.http.patch(url, userWatchData).subscribe((/*response*/) => {
      //
    });
  }

  // Called when a check-question video ends: use the multiple-questions flow when the course has it
  // enabled, otherwise fall back to the single check question.
  triggerVideoQuestions() {
    if (!this.videoData.has_check_question) {
      return;
    }
    if (this.hasMultipleQuestionsAfterVideo && this.videoQuestionSetUrl) {
      this.loadQuestionSet(this.videoQuestionSetUrl, this.videoData.id);
    } else if (this.videoQuestionsUrl) {
      this.loadQuestions(this.videoQuestionsUrl, this.videoData.id);
    }
  }

  loadQuestions(url: string, videoListId?: ID) {
    url = url.replace('{videoListId}', videoListId as string);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.http.get(url).subscribe((response: any) => {
      this.openQuestionDialog([response.data] as Question[]);
    });
  }

  loadQuestionSet(url: string, videoListId?: ID) {
    url = url.replace('{videoListId}', videoListId as string);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.http.get(url).subscribe((response: any) => {
      this.openQuestionSetDialog(response.data, url);
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  openQuestionSetDialog(data: any, questionSetBaseUrl: string) {
    const dialogRef = this.dialog.open(VideoQuestionSetDialogComponent, {
      data: {
        questions: data?.questions,
        settings: data?.settings,
        questionSetBaseUrl: questionSetBaseUrl,
      },
    });

    this.addSubscription(dialogRef.afterClosed()).subscribe((result) => {
      if (result == 'repeatVideo') {
        this.player?.setCurrentTime(0);
      } else if (result == 'nextVideo') {
        this.nextVideo.emit(true);
      }
    });
  }

  openQuestionDialog(questions: Question[]) {
    //
    const dialogRef = this.dialog.open(QuestionDialogComponent, {
      data: {
        questions: questions,
        questionBaseUrl: this.videoQuestionsUrl.replace(
          '{videoListId}',
          this.videoData.id,
        ),
      },
    });

    this.addSubscription(dialogRef.afterClosed()).subscribe((result) => {
      //
      if (result == 'repeatVideo') {
        this.player?.setCurrentTime(0);
      } else if (result == 'nextVideo') {
        this.nextVideo.emit(true);
      }
      // if (result.action.index === 'confirm') {
      //   if (result.data == 'repeatVideo') {
      //     this.player.setCurrentTime(0);
      //   } else if (result.data == 'nextVideo') {
      //     // this.newVideo(this.videoData.next_video_id);
      //   }
      // }
    });
  }

  initVideoWatchTime() {
    if (this.videoData?.video_type.toLowerCase() === 'vimeo') {
      const iframe = this.el.nativeElement.querySelector('iframe');
      this.player = new Vimeo(iframe);
      let videoProgress = this.localWatchedSeconds;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      let lastLogTime = 0;
      this.watchedSeconds = this.localWatchedSeconds;

      const syncProgressInterval = this.videoData.sync_progress_interval
        ? this.videoData.sync_progress_interval
        : 30;

      if (this.isAutoPlayVideo) {
        this.player.play();
      }

      if (!this.videoData?.user_data?.is_completed) {
        this.player?.setCurrentTime(videoProgress);
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.timeUpdateHandler = (data: any) => {
        //

        if (data.duration == data.seconds) {
          this.videoCompleted = true;
          this.cdr.markForCheck();
        }
        //avoid progress cheating by seeking. If video has now learning time tracking, allow seeking
        if (
          this.videoData.is_learning_time_tracking &&
          data.seconds > this.watchedSeconds + 1
        ) {
          this.player?.setCurrentTime(videoProgress);
          return;
        }
        //
        //sync progress every X seconds
        if (
          this.videoWatchUrl &&
          Math.round(data.seconds % syncProgressInterval) === 0
        ) {
          this.syncVideoProgress(this.videoWatchUrl, this.videoData.id);
          lastLogTime = data.seconds;
        }
        videoProgress =
          data.seconds > videoProgress ? data.seconds : videoProgress;

        if (
          !this.videoData.user_data?.is_completed &&
          data.seconds == videoProgress
        ) {
          this.updateVideoWatchTimeList(this.videoData.id, videoProgress);
        }

        //update watched seconds
        if (videoProgress > this.watchedSeconds) {
          this.watchedSeconds = videoProgress;
        }
      };
      this.player.on('timeupdate', this.timeUpdateHandler);
      this.player.on('play', () => this.trackVideoStartOnce());

      this.endedEventHandler = () => {
        this.trackingService.trackVideoEnd(
          String(this.videoData?.id),
          this.watchedSeconds,
          this.videoWatchUrl,
        );
        this.syncVideoProgress(this.videoWatchUrl, this.videoData.id);
        this.triggerVideoQuestions();
      };
      this.player.on('ended', this.endedEventHandler);
      // this.videoCompleted = true;
    } else if (this.videoData?.video_type.toLowerCase() === 'youtube') {
      // Extract YouTube video ID from URL
      const videoId = this.extractYouTubeVideoId(this.videoData.video_url);

      if (!videoId) {
        return;
      }

      // Initialize YouTube player with the official API
      this.player = new YT.Player(`youtube-player-${this.videoData.id}`, {
        height: '100%',
        width: '100%',
        videoId: videoId,
        playerVars: {
          autoplay: this.isAutoPlayVideo ? 1 : 0,
          controls: 1,
          modestbranding: 1,
          rel: 0,
          showinfo: 0,
        },
        events: {
          onStateChange: this.onYoutubePlayerStateChange.bind(this),
          onReady: this.onYoutubePlayerReady.bind(this),
        },
      });
    }
  }

  replayVideo() {
    this.videoCompleted = false;
    this.replay = true;
    this.isAutoPlayVideo = true;
    this.videoStartTracked = false;
  }

  playNextVideo() {
    this.nextVideo.emit(true);
  }

  openNextLesson() {
    this.nextLesson.emit();
  }

  getVideoFromLocalStorage(id: string) {
    const video = this.videosWatchTimeList.videos_watch_time_list.find(
      (el: UserVideosWatchTimeListItem) => el.id == id,
    );
    this.localWatchedSeconds = video?.watched_seconds ?? 0;
  }

  updateVideoWatchTimeList(id: string, watchedSeconds: number) {
    const index = this.videosWatchTimeList.videos_watch_time_list.findIndex(
      (el: UserVideosWatchTimeListItem) => el.id == id,
    );
    this.videosWatchTimeList.videos_watch_time_list[index].watched_seconds =
      watchedSeconds;

    localStorage.setItem(
      'videosWatchTimeList',
      JSON.stringify(this.videosWatchTimeList),
    );
  }

  onYoutubePlayerReady() {
    if (
      !this.videoData?.user_data?.is_completed &&
      this.localWatchedSeconds > 0
    ) {
      this.player.seekTo(this.localWatchedSeconds, true);
    }

    this.initYoutubeVideoWatchTime();
  }

  onYoutubePlayerStateChange(event: YouTubeStateChangeEvent) {
    if (event.data === 1) {
      // YT.PlayerState.PLAYING
      this.trackVideoStartOnce();
    }
    if (event.data === 0) {
      this.watchedSeconds += 1;
      this.trackingService.trackVideoEnd(
        String(this.videoData?.id),
        this.watchedSeconds,
        this.videoWatchUrl,
      );
      this.syncVideoProgress(this.videoWatchUrl, this.videoData.id);
      this.triggerVideoQuestions();
    }
  }

  initYoutubeVideoWatchTime() {
    let videoProgress = this.localWatchedSeconds;
    let lastLogTime = 0;
    this.watchedSeconds = this.localWatchedSeconds;

    const syncProgressInterval = this.videoData.sync_progress_interval ?? 30;

    // YouTube-Player spielt automatisch, wenn gesetzt
    if (this.isAutoPlayVideo) {
      this.player.playVideo();
    }

    // Zeit-Updates alle 1 Sekunde per setInterval
    this.timeUpdateInterval = setInterval(() => {
      try {
        // Check if player is ready and has valid methods
        if (!this.player || typeof this.player.getCurrentTime !== 'function') {
          return;
        }

        const secondsRaw = this.player.getCurrentTime();

        if (
          secondsRaw === null ||
          secondsRaw === undefined ||
          isNaN(secondsRaw)
        ) {
          return;
        }

        const seconds = Math.floor(secondsRaw);

        const durationRaw = this.player.getDuration();

        if (
          durationRaw === null ||
          durationRaw === undefined ||
          isNaN(durationRaw)
        ) {
          return;
        }

        const duration = Math.floor(durationRaw);

        if (duration === seconds && duration > 0) {
          this.videoCompleted = true;
          this.cdr.markForCheck();
        }

        if (
          this.videoData.is_learning_time_tracking &&
          seconds > this.watchedSeconds + 1
        ) {
          this.player.seekTo(videoProgress, true);
          return;
        }

        if (
          this.videoWatchUrl &&
          seconds !== lastLogTime &&
          seconds % syncProgressInterval === 0
        ) {
          this.syncVideoProgress(this.videoWatchUrl, this.videoData.id);
          lastLogTime = seconds;
        }

        videoProgress = seconds > videoProgress ? seconds : videoProgress;

        if (
          !this.videoData.user_data?.is_completed &&
          seconds === videoProgress
        ) {
          this.updateVideoWatchTimeList(this.videoData.id, videoProgress);
        }

        if (videoProgress > this.watchedSeconds) {
          this.watchedSeconds = videoProgress;
          this.cdr.markForCheck();
        }
      } catch (error) {
        console.error(error);
      }
    }, 1000);
  }

  private extractYouTubeVideoId(url: string): string | null {
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    if (match && match[2].length === 11) {
      return match[2];
    }
    return null;
  }
}
